/**
 * Debug logging for the Image Viewer / Annotator component tree.
 *
 * Uses the shared createDebugLogger from @videa/utils (toggled by VITE_DEBUG=true
 * in .env.local or via localStorage.setItem('VITE_DEBUG', 'true') at runtime).
 *
 * Additionally exposes render-count tracking that warns in the console if a
 * component renders excessively within a short window — this warning fires
 * regardless of the debug flag so it surfaces in any environment.
 *
 * View render counts in browser console:  window.__IV_RENDER_COUNTS__
 * Reset render counts:                    window.__IV_RESET_COUNTS__()
 */
import { createDebugLogger } from '@videa/utils';

interface DebugLogger {
  log: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

// Shared logger instances for each layer of the component tree
export const annotatorLog: DebugLogger = createDebugLogger('Annotator');
export const multiFileViewerLog: DebugLogger =
  createDebugLogger('MultiFileViewer');
export const findingViewerLog: DebugLogger = createDebugLogger('FindingViewer');
export const cornerstoneLog: DebugLogger = createDebugLogger('Cornerstone');
export const layoutViewerLog: DebugLogger = createDebugLogger('LayoutViewer');

// ---------------------------------------------------------------------------
// Render-count tracking (always active — warns on excessive renders)
// ---------------------------------------------------------------------------
const WARN_THRESHOLD = 50;
const WARN_WINDOW_MS = 5000;

const renderCounts: Record<string, number> = {};
const renderTimestamps: Record<string, number[]> = {};

if (typeof window !== 'undefined') {
  (window as any).__IV_RENDER_COUNTS__ = renderCounts;
  (window as any).__IV_RESET_COUNTS__ = () => {
    Object.keys(renderCounts).forEach((k) => (renderCounts[k] = 0));
    Object.keys(renderTimestamps).forEach((k) => (renderTimestamps[k] = []));
    // eslint-disable-next-line no-console
    console.log('[ImageViewer] Render counts reset');
  };
}

/**
 * Call at the top of a component body to track renders.
 * Automatically warns if the component renders >= WARN_THRESHOLD times within
 * WARN_WINDOW_MS regardless of the debug flag.
 *
 * When debug logging is enabled it also logs every render.
 */
export function trackRender(
  logger: DebugLogger,
  componentName: string,
  extraContext?: Record<string, unknown>,
): void {
  renderCounts[componentName] = (renderCounts[componentName] || 0) + 1;
  const count = renderCounts[componentName];

  const now = Date.now();
  if (!renderTimestamps[componentName]) renderTimestamps[componentName] = [];
  const ts = renderTimestamps[componentName];
  ts.push(now);
  while (ts.length > 0 && ts[0] < now - WARN_WINDOW_MS) ts.shift();

  if (ts.length >= WARN_THRESHOLD && ts.length % 10 === 0) {
    logger.warn(
      `EXCESSIVE RENDERS: ${ts.length} renders in ${WARN_WINDOW_MS}ms (total: ${count})`,
      extraContext ?? '',
    );
  }

  logger.log(`render #${count}`, extraContext ?? '');
}
